// src/pages/QuoteForm.jsx
// Calls quoteM — your existing PHP function signature:
// { id, corp_id, user_id, creator_id, customer, name, pono, status,
//   invoiced, withtax, i_date, comment, quoteItem: [...] }
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Quotes, Customers, Products, Users } from '../services/api';
import { useAuth, ROLES } from '../context/AuthContext';

const newItem = () => ({ id: 0, product_id: 0, product_name: '', manufacture: '',
  sku: '', price: '', min_price: 0, max_price: 0, tax: 18, qty: 1, pro_description: '' });

export default function QuoteForm() {
  const { id }        = useParams();
  const [sp]          = useSearchParams();
  const navigate      = useNavigate();
  const { user }      = useAuth();
  const isEdit        = Boolean(id);

  const [form, setForm] = useState({
    id: 0, corp_id: user.corp_id, creator_id: user.id,
    customer: sp.get('cus') || '', name: '', pono: '',
    status: 1, invoiced: 0, withtax: 1,
    i_date: new Date().toISOString().slice(0,10), comment: '',
  });
  const [items, setItems]       = useState([newItem()]);
  const [customers, setCusts]   = useState([]);
  const [products, setProducts] = useState([]);
  const [skuQ, setSkuQ]         = useState('');
  const [err, setErr]           = useState('');
  const [saving, setSaving]     = useState(false);
  const [busy, setBusy]         = useState(isEdit);

  useEffect(() => {
    Promise.all([Customers.list(), Products.list()]).then(([c, p]) => {
      setCusts(c); setProducts(p);
    });
    if (isEdit) {
      Quotes.detail(+id).then(q => {
        if (!q) return;
        setForm({
          id: q.id, corp_id: user.corp_id, creator_id: user.id,
          customer: q.customer, name: q.name || '', pono: q.pono || '',
          status: q.status, invoiced: q.invoiced, withtax: q.withtax,
          i_date: q.i_date?.slice(0,10) || q.date_entered?.slice(0,10) || new Date().toISOString().slice(0,10),
          comment: q.comment || '',
        });
        if (q.quoteItem?.length) setItems(q.quoteItem.map(i => ({ ...newItem(), ...i, id: i.id || 0 })));
      }).finally(() => setBusy(false));
    }
  }, [id]);

  // ── Product search ─────────────────────────────────────────
  const addBySKU = () => {
    const found = products.find(p =>
      p.sku?.toLowerCase() === skuQ.toLowerCase() ||
      p.product_name?.toLowerCase().includes(skuQ.toLowerCase())
    );
    if (!found) { setErr(`Product "${skuQ}" not found`); return; }
    setItems(prev => [...prev, { ...newItem(), product_id: found.id,
      product_name: found.product_name, manufacture: found.manufacture,
      sku: found.sku, price: found.min_price, min_price: found.min_price,
      max_price: found.max_price, tax: found.tax }]);
    setSkuQ(''); setErr('');
  };

  const setItem = (idx, k, v) => setItems(prev => {
    const n = [...prev];
    n[idx] = { ...n[idx], [k]: v };
    if (k === 'price' && parseFloat(v) < n[idx].min_price) {
      setErr(`Price below minimum (₹${n[idx].min_price}) for ${n[idx].sku}`);
    } else if (k === 'price') setErr('');
    return n;
  });

  // ── Totals ──────────────────────────────────────────────────
  const subtotal  = items.reduce((s, i) => s + (parseFloat(i.price)||0) * (parseInt(i.qty)||0), 0);
  const taxAmt    = form.withtax==1
    ? items.reduce((s, i) => s + (parseFloat(i.price)||0) * (parseInt(i.qty)||0) * ((parseFloat(i.tax)||0)/100), 0)
    : 0;
  const grandTotal = subtotal + taxAmt;

  // ── Save ────────────────────────────────────────────────────
  const save = async (asInvoice = false) => {
    if (!form.customer) { setErr('Please select a customer'); return; }
    if (items.every(i => !i.product_id)) { setErr('Add at least one product'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        invoiced  : asInvoice ? 1 : form.invoiced,
        status    : asInvoice ? 2 : form.status,
        quoteItem : items.filter(i => i.product_id || i.id),
      };
      const qid = await Quotes.save(payload);
      navigate(`/quotes/${qid || id}`);
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  if (busy) return <div className="spinner-wrap"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <div className="page-title">{isEdit ? 'Edit Quote' : 'New Quote'}</div>
        {isEdit && [ROLES.ADMIN, ROLES.MANAGER].includes(user.ryts) && (
          // <button className="btn btn-success btn-sm"
          //   onClick={() => Quotes.setStatus({ id, invoiced:1, status:2 }).then(() => navigate('/invoices'))}>
          //   → Convert to Invoice
          // </button>
          ""
        )}
      </div>

      {err && <div className="alert alert-error">{err}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.2rem' }}>
        {/* Left — details */}
        <div className="card">
          <div style={{ fontWeight:600, marginBottom:'1rem' }}>Quote Details</div>
          <label style={{ marginBottom:'.9rem' }}>
            Customer *
            <select value={form.customer} onChange={f('customer')}>
              <option value="">-- Select --</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company} — {c.name}</option>)}
            </select>
          </label>
          <label style={{ marginBottom:'.9rem' }}>
            Quote Name
            <input value={form.name} onChange={f('name')} placeholder="Auto-generated if blank" />
          </label>
          <label style={{ marginBottom:'.9rem' }}>
            PO Number
            <input value={form.pono} onChange={f('pono')} />
          </label>
          <label style={{ marginBottom:'.9rem' }}>
            Date
            <input type="date" value={form.i_date} onChange={f('i_date')} />
          </label>
          <div style={{ display:'flex', gap:'1.5rem', marginBottom:'.9rem' }}>
            <label style={{ flexDirection:'row', alignItems:'center', gap:'.4rem', cursor:'pointer' }}>
              <input type="checkbox" style={{ width:'auto' }} checked={form.withtax==1}
                onChange={e => setForm(p => ({ ...p, withtax: e.target.checked ? 1 : 0 }))} />
              Include GST
            </label>
            <label style={{ flexDirection:'row', alignItems:'center', gap:'.4rem', cursor:'pointer' }}>
              <input type="checkbox" style={{ width:'auto' }} checked={form.status==2}
                onChange={e => setForm(p => ({ ...p, status: e.target.checked ? 2 : 1 }))} />
              Proforma Invoice
            </label>
          </div>
          <label>
            Notes
            <textarea rows={3} value={form.comment} onChange={f('comment')} />
          </label>
        </div>

        {/* Right — line items */}
        <div className="card">
          <div style={{ fontWeight:600, marginBottom:'1rem' }}>Line Items</div>
          <div style={{ display:'flex', gap:'.5rem', marginBottom:'1rem' }}>
            <input placeholder="Search SKU or product name…" value={skuQ}
              onChange={e => setSkuQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addBySKU()}
              style={{ flex:1 }} type="text" list="skus" />
            <datalist id="skus">
              {products.map(p => (
                <option key={p.id} value={p.sku}>{p.product_name} (₹{p.min_price})</option>
              ))}
            </datalist>
            <button className="btn btn-primary btn-sm" onClick={addBySKU}>+ Add</button>
          </div>

          <div style={{ maxHeight:360, overflowY:'auto', display:'flex', flexDirection:'column', gap:'.6rem', marginBottom:'1rem' }}>
            {items.map((item, idx) => (
              <div key={idx} style={{ background:'var(--bg)', borderRadius:8, padding:'.7rem', border:'1px solid var(--border)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'.5rem' }}>
                  <span style={{ fontWeight:600, fontSize:'.88rem' }}>{item.product_name || 'New Item'}</span>
                  <div style={{ display:'flex', gap:'.4rem', alignItems:'center' }}>
                    <code style={{ fontSize:'.72rem' }}>{item.sku}</code>
                    <button className="btn-icon" style={{ color:'var(--red)' }}
                      onClick={() => setItems(p => p.filter((_,i) => i !== idx))}>✕</button>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'70px 1fr 70px', gap:'.4rem', marginBottom:'.4rem' }}>
                  <label style={{ fontSize:'.75rem' }}>Qty
                    <input type="number" min="0" value={item.qty}
                      onChange={e => setItem(idx, 'qty', e.target.value)} />
                  </label>
                  <label style={{ fontSize:'.75rem' }}>
                    Price ₹ <span style={{ color:'var(--faint)', fontWeight:400 }}>(min {item.min_price})</span>
                    <input type="number" step="0.01" value={item.price}
                      onChange={e => setItem(idx, 'price', e.target.value)}
                      className={parseFloat(item.price) < item.min_price && item.price !== '' ? 'input-error' : ''} />
                  </label>
                  <label style={{ fontSize:'.75rem' }}>Tax %
                    <input type="number" value={item.tax}
                      onChange={e => setItem(idx, 'tax', e.target.value)}
                      disabled={!form.withtax} />
                  </label>
                </div>
                <label style={{ fontSize:'.75rem' }}>
                  Description
                  <input value={item.pro_description}
                    onChange={e => setItem(idx, 'pro_description', e.target.value)}
                    placeholder="Optional…" />
                </label>
                <div style={{ textAlign:'right', color:'var(--accent)', fontWeight:700, fontSize:'.9rem', marginTop:'.3rem' }}>
                  ₹{((parseFloat(item.price)||0) * (parseInt(item.qty)||0) * (form.withtax ? 1 + (parseFloat(item.tax)||0)/100 : 1)).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:'.7rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', color:'var(--muted)', marginBottom:'.3rem' }}>
              <span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span>
            </div>
            {form.withtax==1 && (
              <div style={{ display:'flex', justifyContent:'space-between', color:'var(--muted)', marginBottom:'.3rem' }}>
                <span>GST</span><span>₹{taxAmt.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'1.05rem', color:'var(--text)' }}>
              <span>Total</span><span>₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {user.ryts != 3 && (
        <div style={{ display:'flex', gap:'.8rem', marginTop:'1.2rem' }}>
          <button className="btn btn-primary" onClick={() => save(false)} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Update Quote' : 'Save Quote'}
          </button>
          {[ROLES.ADMIN, ROLES.MANAGER].includes(user.ryts) && (
          <button className="btn btn-success" onClick={() => save(true)} disabled={saving}>Save as Invoice</button>
        )}
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
      </div>
      )}
    </div>
  );
}